import React from 'react';
import { useTranslation } from 'react-i18next';
import { CollocationDiscoveryItem } from '../../../types/lesson';
import { useLessonPersistedState } from '../useLessonPersistedState';
import TranslateToggle from './TranslateToggle';

interface Props {
    lessonId?: number;
    items: CollocationDiscoveryItem[];
    teacherMode: boolean;
    onWordAnswered?: (word: string, correct: boolean, hintUsed?: boolean) => void;
    sourceLanguage?: string;
    targetLanguage?: string;
}

const CollocationDiscoverySection: React.FC<Props> = ({ lessonId, items, teacherMode, onWordAnswered, sourceLanguage, targetLanguage }) => {
    const { t } = useTranslation('lesson');
    const [picked, setPicked] = useLessonPersistedState<Record<string, boolean>>(lessonId, 'collocation.picked', {});
    const [showTranslation, setShowTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'collocation.showTranslation', {});
    const [explanationFallback, setExplanationFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'collocation.explanationFallback', {});
    // Pre-answer icons (head_word, candidates) can hint at which candidate is natural — opening
    // one before the item is picked flags it for a reduced score at Finish.
    const [showHeadTranslation, setShowHeadTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'collocation.showHeadTranslation', {});
    const [headFallback, setHeadFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'collocation.headFallback', {});
    const [showCandidatesTranslation, setShowCandidatesTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'collocation.showCandidatesTranslation', {});
    const [candidatesFallback, setCandidatesFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'collocation.candidatesFallback', {});
    const [hintUsed, setHintUsed] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'collocation.hintUsed', {});

    const isAnswered = (i: number) => Object.keys(picked).some((k) => k.startsWith(`${i}-`));
    const markHint = (i: number) => {
        if (isAnswered(i)) return;
        setHintUsed((prev) => (prev[i] ? prev : { ...prev, [i]: true }));
    };

    // Computed from the current `picked` in the event handler, not inside a setPicked updater:
    // reporting the answer (onWordAnswered → LessonPlayer's setState) is a side effect, and
    // updaters must be pure — React may run them during render or twice (StrictMode).
    const pick = (i: number, key: string, headWord: string) => {
        const next = { ...picked, [key]: true };
        // The prompt explicitly invites clicking every natural-sounding candidate for this
        // head word, so a single click's own correctness isn't the item's score — score the
        // item as a whole: correct only if every candidate clicked so far (across possibly
        // several clicks) is natural. Recomputed from `next` on every click (not tracked as
        // separate state) so it's order-independent — one wrong click among several correct
        // ones still marks the item wrong, and it stays wrong even if the learner goes on to
        // click more natural candidates afterward.
        const itemCorrect = items[i].candidates.every((cand, ci) => !next[`${i}-${ci}`] || cand.is_natural);
        setPicked(next);
        onWordAnswered?.(headWord, itemCorrect, hintUsed[i]);
    };

    return (
        <div className="lesson-panel">
            <div>
                <div className="lesson-panel-kicker">{t('sections.collocation.kicker', 'What sounds natural?')}</div>
                <h2>{t('sections.collocation.title', 'What Sounds Natural?')}</h2>
                <p className="lesson-panel-desc">
                    {t('sections.collocation.desc', 'Click every combination that sounds natural.')}
                </p>
            </div>
            <div className="lesson-qgrid">
                {items.map((item, i) => (
                    <div className="lesson-qcard" key={i}>
                        <div className="lesson-qnum lesson-word-target">
                            {item.head_word.toUpperCase()}
                            <TranslateToggle
                                text={item.head_word}
                                fallback={headFallback[i]}
                                onFallbackFetched={(text) => setHeadFallback((prev) => ({ ...prev, [i]: text }))}
                                sourceLanguage={sourceLanguage}
                                targetLanguage={targetLanguage}
                                shown={!!showHeadTranslation[i]}
                                onToggle={() => { markHint(i); setShowHeadTranslation((prev) => ({ ...prev, [i]: !prev[i] })); }}
                            />
                        </div>
                        <div className="lesson-candidates">
                            {item.candidates.map((cand, ci) => {
                                const key = `${i}-${ci}`;
                                const isPicked = picked[key] || teacherMode;
                                let cls = 'lesson-cand';
                                if (isPicked) cls += cand.is_natural ? ' right' : ' wrong';
                                return (
                                    <button key={ci} className={cls} disabled={isPicked} onClick={() => pick(i, key, item.head_word)}>
                                        {cand.phrase}
                                    </button>
                                );
                            })}
                            <TranslateToggle
                                text={item.candidates.map((c) => c.phrase).join(' / ')}
                                fallback={candidatesFallback[i]}
                                onFallbackFetched={(text) => setCandidatesFallback((prev) => ({ ...prev, [i]: text }))}
                                sourceLanguage={sourceLanguage}
                                targetLanguage={targetLanguage}
                                shown={!!showCandidatesTranslation[i]}
                                onToggle={() => { markHint(i); setShowCandidatesTranslation((prev) => ({ ...prev, [i]: !prev[i] })); }}
                            />
                        </div>
                        {(isAnswered(i) || teacherMode) && (
                            <div className="lesson-reveal lesson-word-target">
                                <div className="lesson-reveal-explanation">
                                    {item.explanation}
                                    <TranslateToggle
                                        text={item.explanation}
                                        native={item.explanation_native}
                                        fallback={explanationFallback[i]}
                                        onFallbackFetched={(text) => setExplanationFallback((prev) => ({ ...prev, [i]: text }))}
                                        sourceLanguage={sourceLanguage}
                                        targetLanguage={targetLanguage}
                                        shown={!!showTranslation[i]}
                                        onToggle={() => setShowTranslation((prev) => ({ ...prev, [i]: !prev[i] }))}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CollocationDiscoverySection;
